import { DeleteOutlined } from "@ant-design/icons";
import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { FindDashboardItemDTO, FindGrafanaItem, FindGrafanaItemsDTO, FindItem, GrafanaTreeDTO, GrafanaTreeItem } from "@dto/dashboard.dto";
import { DashboardContextMenu, MenuDataNode } from "@parts/dashboard/select/DashboardContextMenu";
import { Button, Checkbox, Collapse, Divider, Space, Tooltip, TreeSelect } from "antd";
import { SafeKey } from "antd/es/table/interface";
import axios, { AxiosError } from "axios";
import React from "react";
import { FaPlus } from "react-icons/fa";
import { DashTreeSourceNode, loadDashboardTree, mapDashTree } from "src/component/dashboardTree/dashboardTreeSource";
import { XmModal } from "src/component/modal/XmModal";
import { GROUPS_DIVIDER_LABEL } from "src/content/configuration/dashboard/graphBuilder/graphBuilderTypes";
import { getApi, postApi } from "src/util/apiCalls";
import { XmAsyncButton } from "../../../component/asyncButton/XmAsyncButton";
import { API_URL } from "../../../data/Api";
import { MenuNode } from "../../../model/MenuNode";
import { Log } from "../../../service/log.service";
import { GLOB } from "../../../util/Glob";
import "./AbstractDashboardSelect.less";

export interface CommonSelectProps {
  tabName?: string;
  subTabName?: string;
  tabId?: string;
  dashUrl?: string;
  dashboardItems?: FindItem[];
  setDashboardItems?: (fg: FindItem[]) => void;
}

export interface DashboardSelectProps {
  common: CommonSelectProps;
  timestamp: number;
}

export interface DashboardSelectState {
  selects: DashboardSelect[];
  grafanaSelects: DashboardSelect[];
  options: MenuDataNode[];
  grafanaOptions: MenuDataNode[];
  modalOpen: boolean;
  grafanaTrial: boolean;
  dashboardItems: FindItem[];
  grafanaItems: FindGrafanaItem[];
}

export class DashboardSelect {
  value: string;
  selected: MenuDataNode;
  dashId: string;
  open: boolean;
  id: string;
  /** Identifies the select before it has a server id; also keys its pending operation. */
  selectId: number;
  expandedKeys: SafeKey[];
  isAllowed?: boolean;
  searching?: boolean;
  legend?: boolean;
  /** Its pending operation was rejected by the server on the last save. */
  failed?: boolean;
}

/** Dashboards keep their folder path but lose their group children. */
function stripGroups(nodes: DashTreeSourceNode[]): DashTreeSourceNode[] {
  return nodes.filter(n => n.type !== 'group')
    .map(n => n.children ? { ...n, children: stripGroups(n.children) } : n);
}

enum PanelID {
  DASHBOARD = 'dashboard', GRAFANA = 'grafana'
}

export default abstract class AbstractDashboardSelect<P extends DashboardSelectProps = DashboardSelectProps, S extends DashboardSelectState = DashboardSelectState> extends React.Component<P, S> {

  static readonly GRAFANA_PREFIX = PanelID.GRAFANA + '-';

  private readonly contextMenu: DashboardContextMenu;
  private readonly abortCtrl = new AbortController();
  private selectCount = 0;
  protected operations: Record<string, () => Promise<unknown>> = {};
  protected grafanaLoaded = false;
  protected showLegendCheckbox = false;
  private activePanel = PanelID.DASHBOARD;
  readonly DASHBOARD = 'Dashboard';

  protected get noGroup() { return false; }
  protected get grafanaEnabled() { return true; }
  protected get itemLabel() { return 'graph'; }

  constructor(props: P) {
    super(props);
    this.state = {
      selects: [], grafanaSelects: [],
      options: [], grafanaOptions: [],
      modalOpen: false, grafanaTrial: false,
      dashboardItems: [], grafanaItems: []
    } as S;
    this.contextMenu = new DashboardContextMenu(() => this.state,
      this.setState.bind(this) as typeof this.setState.bind,
      (item) => item.dashboard,
      this.noGroup ? null : item => item.group,
      this.onDelete.bind(this) as typeof this.onDelete, null, true);
  }

  componentWillUnmount(): void {
    this.abortCtrl.abort();
  }

  componentDidUpdate(prevProps: Readonly<P>): void {
    if (!this.props.timestamp || prevProps.timestamp === this.props.timestamp) return;

    this.grafanaLoaded = false;
    this.operations = {};

    const existing = this.props.common?.dashboardItems?.filter(fg => !this.props['interval'] || fg.interval === this.props['interval']);
    const selects = existing?.length ? existing.map(fg => this.createSelect(fg)) : [this.createSelect()];

    if (GLOB.userInfo.readonly) {
      const noGroupGraphs = new Set(
        this.props.common?.dashboardItems?.filter(fg => (fg.group_id ?? null) === null)
          .map(fg => fg.dashboard_item_id)
      );
      for (const select of selects) {
        if (select.dashId !== undefined) {
          select.isAllowed = noGroupGraphs.has(select.id);
        }
      }
    }

    this.setState({
      dashboardItems: [...(this.props.common?.dashboardItems || [])],
      options: [],
      modalOpen: true, selects,
      grafanaSelects: []
    });
    void this.loadDashboardOptions();
  }

  /** Why the item cannot be placed in dashboards shared with the given user group; undefined when it can. */
  protected groupPlacementDenial(_groupId: number): string {
    return undefined;
  }

  protected abstract updateItem(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode, dashboardItems: S['dashboardItems'], path: string);

  protected abstract addItem(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode, dashboardItems: S['dashboardItems'], path: string);

  protected abstract addGrafana(ds: DashboardSelect, dash: MenuDataNode);

  protected abstract showItemInDashboardWarn(dash: MenuDataNode, ds: DashboardSelect);

  protected abstract postToGrafana(dashboard: MenuDataNode): Promise<unknown>;

  protected abstract postToDashboard(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode): Promise<unknown>;

  protected abstract updateInDashboard(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode): Promise<unknown>;

  /** Persists option-only changes (e.g. legend) of an item that stays in its group. */
  protected updateOptionsInDashboard(ds: DashboardSelect): Promise<unknown> {
    return Promise.resolve();
  }

  private static closed(selects: DashboardSelect[]) {
    return selects.map(c => ({ ...c, open: false }));
  }

  private static isDisabled(ds: DashboardSelect) {
    return !(ds.isAllowed ?? true);
  }

  // selects get cloned on re-render, so deferred operations must re-resolve by key
  private liveSelect(ds: DashboardSelect) {
    return this.state.selects.find(s => s.selectId === ds.selectId) ?? ds;
  }

  /** Flags one select, or every select when no id is given; operation keys do not tell the two lists apart. */
  private setFailed(failed: boolean, selectId?: number) {
    const mark = (list: DashboardSelect[]) => list.map(s => selectId === undefined || s.selectId === selectId ? { ...s, failed } : s);
    this.setState(prev => ({ selects: mark(prev.selects), grafanaSelects: mark(prev.grafanaSelects) }));
  }

  private onLegendChange(ds: DashboardSelect, legend: boolean) {
    ds.legend = legend;
    if (ds.id && !this.operations[ds.selectId]) {
      this.operations[ds.selectId] = () => this.updateOptionsInDashboard(this.liveSelect(ds));
    }
    this.setState(prev => ({ selects: [...prev.selects] }));
  }

  /** Whether the item is the one this select already stands for — saved under its id, or pending under its selectId. */
  protected isOwnItem(ds: DashboardSelect, item: { dashboard_item_id?: string; }) {
    return (ds.id != null && item.dashboard_item_id === ds.id) || item.dashboard_item_id === String(ds.selectId);
  }

  protected removeItem(ds: DashboardSelect, dashboardItems: S['dashboardItems']) {
    const idx = dashboardItems.findIndex(fg => fg.dashboard_item_id === ds.id);
    if (idx >= 0)
      dashboardItems.splice(idx, 1);
    else {
      const key = String(ds.selectId);
      const idxNew = dashboardItems.findIndex(fg => fg.dashboard_item_id === key);
      if (idxNew >= 0) {
        dashboardItems.splice(idxNew, 1);
      }
    }
  };

  protected createSelect(fg?: FindItem): DashboardSelect {
    const groupTitle = fg?.group_id == null ? '' : '/' + GLOB.menuService.getDashMenu().children.find(mdn => mdn.group_id === fg.group_id)?.titleText;
    const groupId = fg?.dashboard_group.dashboard_group_id;
    return {
      open: false,
      legend: fg?.legend,
      expandedKeys: [DashboardContextMenu.ROOT],
      value: fg ? `${this.DASHBOARD}${groupTitle}${fg.path && fg.path !== '/' ? fg.path : ''}/${fg.dashboard.label}/${fg.dashboard_group.label}` : null,
      selected: fg ? {
        key: groupId, value: groupId, itemId: groupId,
        titleText: fg.dashboard_group.label, isLeaf: true, dashboard: false, group: true
      } : null,
      selectId: this.selectCount++,
      id: fg?.dashboard_item_id,
      dashId: fg?.dashboard.dashboard_id
    };
  }

  private createGrafanaSelect(fgg?: FindGrafanaItem): DashboardSelect {
    const uid = fgg?.dashboard.grafana_dashboard_uid;
    return {
      open: false,
      expandedKeys: [],
      value: fgg ? `Grafana / ${fgg.dashboard.label}` : null,
      selected: fgg ? {
        key: uid, value: uid, itemId: uid,
        titleText: fgg.dashboard.label, isLeaf: true, dashboard: true, group: false
      } : null,
      selectId: this.selectCount++,
      id: AbstractDashboardSelect.GRAFANA_PREFIX + fgg?.grafana_item_uid,
      dashId: uid
    };
  }

  private loadDashboardOptions() {
    const { readonly } = GLOB.userInfo;
    return loadDashboardTree(this.abortCtrl.signal).then(src => {
      if (this.noGroup)
        src = stripGroups(src);
      const toNode = (n: DashTreeSourceNode, children: MenuDataNode[]): MenuDataNode => {
        const denial = n.groupId == null ? undefined : this.groupPlacementDenial(n.groupId);
        const mdn: MenuDataNode = {
          key: n.id, value: n.id, itemId: n.id, titleText: n.label, label: null,
          isLeaf: n.type === 'group' || (this.noGroup && n.type === 'dashboard'),
          dashboard: n.type === 'dashboard',
          group: n.type === 'group',
          disabled: n.groupId != null && (readonly || !!denial),
          ...(denial ? { disabledReason: denial } : {}),
          ...(n.groupRoot ? { group_id: n.groupId } : {}),
        };
        if (n.type === 'dashboard' && !children.length && !this.noGroup) {
          children = [{
            disabled: true, label: 'No groups yet', key: n.id + DashboardContextMenu.NO_GROUP,
            value: n.id + DashboardContextMenu.NO_GROUP,
            itemId: DashboardContextMenu.NO_GROUP, group: true, isLeaf: true
          }];
        }
        if (children.length) {
          children.forEach(c => c.parent = mdn);
          mdn.children = children;
        }
        mdn.title = this.contextMenu.createLabel(mdn);
        return mdn;
      };
      const personal = mapDashTree(src.filter(n => n.type !== 'user_group'), toNode);
      const shared = mapDashTree(src.filter(n => n.type === 'user_group'), toNode);
      if (shared.length) {
        personal.push({
          itemId: MenuNode.GROUPS_DIVIDER_KEY, key: MenuNode.GROUPS_DIVIDER_KEY,
          value: MenuNode.GROUPS_DIVIDER_KEY, disabled: true, isLeaf: true, divider: true, titleText: '',
          title: <div>{GROUPS_DIVIDER_LABEL}</div>
        }, ...shared);
      }
      const root: MenuDataNode = {
        titleText: this.DASHBOARD, itemId: DashboardContextMenu.ROOT, key: DashboardContextMenu.ROOT,
        value: DashboardContextMenu.ROOT, isLeaf: false, children: personal
      };
      personal.forEach(c => c.parent = root);
      root.title = this.contextMenu.createLabel(root);
      this.setState(prev => ({
        options: [root],
        selects: prev.selects.map(ds => {
          if (!ds.selected?.value) return ds;
          const chain = this.findRekur(String(ds.selected.value), [root]);
          if (!chain) return ds;
          return { ...ds, expandedKeys: chain.slice(1).map(n => n.key as SafeKey) };
        })
      }));
    }, (reason: AxiosError) => Log.error('Failed to load dashboard tree!', reason));
  }

  static refreshRekur(item: MenuDataNode, nodes: MenuDataNode[]) {
    if (!item || !nodes)
      return;
    for (const node of nodes) {
      if (DashboardContextMenu.equals(item, node)) {
        if (item.children)
          item.children = [...item.children];
        return nodes.map(mdn => {
          if (DashboardContextMenu.equals(item, mdn)) {
            const itemNew = { ...item };
            if (item.children) {
              item.children.forEach(c => c.parent = itemNew);
            }
            return itemNew;
          }
          return mdn;
        });
      }
      const result = this.refreshRekur(item, node.children);
      if (result) {
        node.children = result;
        return [...nodes];
      }
    }
  }

  private onDelete(item: MenuDataNode) {
    for (const ds of this.state.selects) {
      if (ds.selected?.value === item.value || (ds.dashId != null && (ds.dashId === item.itemId || ds.dashId === item.value))) {
        ds.selected = null;
        ds.value = null;
        ds.id = null;
      }
    }
  }

  private setOpen(open: boolean, ds: DashboardSelect) {
    ds.open = open;
    const toggle = (selects: DashboardSelect[]) => selects.map(c => c === ds ? { ...c } : { ...c, open: false });
    if (this.activePanel === PanelID.GRAFANA)
      this.setState(prev => ({ grafanaSelects: toggle(prev.grafanaSelects) }));
    else
      this.setState(prev => ({ selects: toggle(prev.selects) }));
  }

  private grafanaItemToDataNode(items: GrafanaTreeItem[]): MenuDataNode[] {
    if (!items) return null;
    return items.map(gti => {
      const id = AbstractDashboardSelect.GRAFANA_PREFIX + gti.uid;
      const isDashboard = gti.type === 'dashboard';
      const mdn: MenuDataNode = {
        ...gti, key: id, itemId: id, label: null, titleText: gti.label, value: id, isLeaf: isDashboard,
        dashboard: isDashboard, children: this.grafanaItemToDataNode(gti.children)
      };
      mdn.title = <span>{this.contextMenu.getIconTitle(mdn)}</span>;
      return mdn;
    });
  }

  private removeSelect(ds: DashboardSelect) {
    this.removeItem(ds, this.state.dashboardItems);
    if (ds.id) {
      this.operations[ds.selectId] = () => axios.delete<BasicResponseDTO>(API_URL.DASHBOARD_ITEM + '/' + ds.id).then(() => {
        this.removeItem(ds, this.props.common.dashboardItems);
      }, (reason: AxiosError) => Log.error('Failed to remove graph from dashboard!', reason));
    } else {
      delete this.operations[ds.selectId];
    }
    if (this.activePanel === PanelID.DASHBOARD)
      this.setState(prev => ({ selects: AbstractDashboardSelect.closed(prev.selects.filter(c => c !== ds)) }));
    else
      this.setState(prev => ({ grafanaSelects: AbstractDashboardSelect.closed(prev.grafanaSelects.filter(c => c !== ds)) }));
  }

  private addCascader() {
    if (this.activePanel === PanelID.DASHBOARD)
      this.setState(prev => ({ selects: [...AbstractDashboardSelect.closed(prev.selects), this.createSelect()] }));
    else if (this.activePanel !== PanelID.GRAFANA)
      return;
    else if (!GLOB.grafanaActive)
      Log.warn('Grafana is not configured yet.');
    else if (this.state.grafanaTrial)
      Log.warn('Grafana functionality requires REST API license.');
    else if (!GLOB.userInfo.isAdmin)
      Log.warn('Only admins can export graphs to Grafana.');
    else
      this.setState(prev => ({ grafanaSelects: [...AbstractDashboardSelect.closed(prev.grafanaSelects), this.createSelect()] }));
  }

  /** @returns chain from the matching node up to the root, or undefined */
  private findRekur(value: string, options: MenuDataNode[]): MenuDataNode[] {
    if (!value || !options) return;
    for (const node of options) {
      if (node.value === value) return [node];
      const result = this.findRekur(value, node.children);
      if (result) {
        result.push(node);
        return result;
      }
    }
  }

  private onChange(value: string, ds: DashboardSelect, options: MenuDataNode[]) {
    const found = this.findRekur(value, options);
    if (!found?.[0].isLeaf) return;

    const rootToLeaf = found.toReversed();
    if (found[0].value.startsWith(AbstractDashboardSelect.GRAFANA_PREFIX)) {
      // Grafana has no groups and existing exports cannot be moved
      if (ds.id) return;
      const dash = found[0];
      this.addGrafana(ds, dash);
      this.operations[ds.selectId] = () => this.postToGrafana(dash);
      ds.selected = dash;
    } else {
      const group = this.noGroup ? null : found[0];
      const dash = this.noGroup ? found[0] : found[1];
      const path = rootToLeaf.slice(0, this.noGroup ? -1 : -2).map(mdn => mdn.titleText).join('/');

      // must run before any mutation: a rejected pick may not drop this select's item
      // while its pending operation lives on, or the next select stops seeing the collision
      if (this.showItemInDashboardWarn(dash, ds)) return;

      if (ds.id) {
        this.updateItem(ds, dash, group, this.state.dashboardItems, path);
        this.operations[ds.selectId] = () => this.updateInDashboard(this.liveSelect(ds), dash, group);
      } else {
        this.removeItem(ds, this.state.dashboardItems);
        this.addItem(ds, dash, group, this.state.dashboardItems, path);
        this.operations[ds.selectId] = () => this.postToDashboard(this.liveSelect(ds), dash, group);
      }
      ds.selected = group ?? dash;
    }
    ds.value = rootToLeaf.map(mdn => mdn.titleText).join('/');
    ds.failed = false;
    this.setOpen(false, ds);
  }

  private renderRemoveButton(ds: DashboardSelect, title: string, disabled: boolean) {
    return <Button type="text" title={title} disabled={disabled || this.isGrafana(ds)} icon={<DeleteOutlined />}
      className="xm-abstract-dashboard-select-modal__remove-button hover-danger" onClick={() => this.removeSelect(ds)} />;
  }

  private dropdownRender(menu: React.ReactElement) {
    return <div>
      {menu}
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ margin: '0 0.5em' }}>Right click to create folder{this.noGroup ? ' or dashboard.' : ', dashboard or group.'}</div>
    </div>;
  }

  readonly isGrafana = (ds: DashboardSelect) => {
    return ds?.id?.startsWith(AbstractDashboardSelect.GRAFANA_PREFIX);
  };

  readonly onPanelActivate = (key: PanelID[]) => {
    this.activePanel = key[0];
    this.setState(prev => ({
      grafanaSelects: AbstractDashboardSelect.closed(prev.grafanaSelects),
      selects: AbstractDashboardSelect.closed(prev.selects)
    }));
    if (this.activePanel !== PanelID.GRAFANA || this.grafanaLoaded) return;

    this.grafanaLoaded = true;
    if (!GLOB.grafanaActive || !GLOB.userInfo.isAdmin) return;

    const c = this.props.common;
    postApi<FindGrafanaItemsDTO, FindDashboardItemDTO>(API_URL.DASHBOARD + '/grafana/find',
      {
        tab: c.tabName, subtab: c.subTabName, tabId: c.tabId, url: c.dashUrl,
        interval: this.props['interval'], id: this.props['id']
      }).then(response => {
        const grafanaGraphs = response.data.data || [];
        const grafanaSelects = grafanaGraphs.length ? grafanaGraphs.map(fgg => this.createGrafanaSelect(fgg)) : [this.createSelect()];
        this.setState({ grafanaItems: grafanaGraphs, grafanaSelects });
      }, reason => {
        this.setState({ grafanaTrial: true });
        Log.error('Failed to find Grafana locations!', reason);
      });

    getApi<GrafanaTreeDTO>(API_URL.DASHBOARD_GRAFANA + '/tree', this.abortCtrl.signal).then(response => {
      const opt: GrafanaTreeItem = {
        label: 'Grafana', uid: PanelID.GRAFANA as unknown as number,
        children: response.data.data, type: 'folder'
      };
      this.setState({ grafanaOptions: this.grafanaItemToDataNode([opt]) });
    }, reason => Log.error('Failed to get Grafana tree!', reason));
  };

  private onSave = () => {
    // a finished operation must not run again when a failed sibling keeps the dialog open for a retry
    this.setFailed(false);
    // dashId survives onDelete, so only rows never picked count as empty
    const empty = this.state.selects.find(ds => ds.selected?.value == null && ds.dashId == null && !AbstractDashboardSelect.isDisabled(ds));
    if (this.activePanel === PanelID.DASHBOARD && !Object.keys(this.operations).length && empty) {
      this.setFailed(true, empty.selectId);
      Log.warn(this.noGroup ? 'Select a dashboard' : 'Select a dashboard group');
      return Promise.resolve();
    }
    const pending = Object.entries(this.operations).map(([key, o]) => o().then(
      () => delete this.operations[key],
      reason => { this.setFailed(true, Number(key)); throw reason; }));
    return Promise.all(pending).then(() => {
      setTimeout(() => {
        const dgs = [...(this.props.common.dashboardItems || [])];
        this.props.common.setDashboardItems?.(dgs);
        this.setState({ modalOpen: false, dashboardItems: dgs });
      });
    }, () => {
      // the operation already reported its failure; the dialog stays open and the Save button must settle
    });
  };

  render() {
    const { readonly } = GLOB.userInfo;
    return <XmModal
      open={this.state.modalOpen}
      destroyOnHidden={true}
      onOk={this.onSave}
      onCancel={() => this.setState({ modalOpen: false })}
      className="xm-abstract-dashboard-select-modal"
      footer={<div className="xm-abstract-dashboard-select-modal__footer">
        <Button
          title="Add another dashboard select"
          onClick={() => this.addCascader()}
          className="xm-abstract-dashboard-select-modal__add-button"
          icon={<FaPlus className="xm-abstract-dashboard-select-modal__add-icon" />}
        >
          Add entry
        </Button>
        <div className="xm-abstract-dashboard-select-modal__footer-actions">
          <Button onClick={() => this.setState({ modalOpen: false })}>Cancel</Button>
          <XmAsyncButton type="primary"
            onClickAsync={this.onSave}>Save</XmAsyncButton>
        </div>
      </div>}
    >
      <div className="xm-abstract-dashboard-select-modal__content">
        <Collapse bordered={false} accordion={true} defaultActiveKey={PanelID.DASHBOARD}
          // a lone panel is pinned open, so its clicks must not reach onPanelActivate and unset the active panel
          activeKey={this.grafanaEnabled ? undefined : PanelID.DASHBOARD}
          onChange={this.grafanaEnabled ? this.onPanelActivate : undefined}
          items={[
          {
            key: PanelID.DASHBOARD,
            label: `Show ${this.itemLabel} in dashboards`,
            children: <Space orientation="vertical" className="xm-abstract-dashboard-select-modal__panel-content" onClick={e => {
              const el = e.target as HTMLElement;
              if (el.closest('.ant-tree-select,.ant-select-tree,.ant-dropdown-menu')) return;
              this.setState(prev => ({ selects: AbstractDashboardSelect.closed(prev.selects) }));
            }}>
              {this.state.selects.map(ds => <div key={ds.selectId} className="xm-abstract-dashboard-select-modal__row">
                <TreeSelect
                  className="xm-abstract-dashboard-select-modal__tree-select"
                  labelInValue
                  status={ds.failed ? 'error' : undefined}
                  // undefined makes rc uncontrolled and it shows a clicked dashboard; null only warns once in dev
                  value={ds.selected?.value != null ? { value: ds.selected.value, label: ds.value } : null}
                  disabled={AbstractDashboardSelect.isDisabled(ds)}
                  classNames={{ popup: { root: "xm-dashboard-select" } }}
                  virtual={false}
                  popupRender={menu => this.dropdownRender(menu)}
                  showSearch={{
                    treeNodeFilterProp: 'titleText',
                    // while searching, expansion is handed to the built-in search
                    // auto-expand — controlled treeExpandedKeys would block it
                    onSearch: value => {
                      if (!!value === !!ds.searching) return;
                      ds.searching = !!value;
                      this.setState(prev => ({ selects: [...prev.selects] }));
                    },
                  }}
                  placeholder="Select dashboard"
                  onChange={(v: { value: string; label?: React.ReactNode; }) => {
                    // rc clears the search text on select without firing onSearch — sync the flag
                    ds.searching = false;
                    if (v?.value) {
                      // selection from search bypasses onTreeExpand — re-derive ancestor expansion
                      const chain = this.findRekur(String(v.value), this.state.options);
                      if (chain) ds.expandedKeys = chain.slice(1).map(n => n.key as SafeKey);
                    }
                    this.onChange(v?.value, ds, this.state.options);
                  }}
                  treeData={this.state.options}
                  open={this.state.selects.find(c => c.selectId === ds.selectId)?.open}
                  onClick={ev => {
                    const el = ev.target as Element;
                    // caret click in the search input must not toggle the open dropdown
                    if (ds.open && el.tagName === 'INPUT') return;
                    if (el.closest('.ant-tree-select')) this.setOpen(!ds.open, ds);
                  }}
                  onSelect={(value, option) => {
                    const optKey = option.key as SafeKey;
                    ds.expandedKeys = ds.expandedKeys.includes(optKey)
                      ? ds.expandedKeys.filter(k => k !== optKey)
                      : [...ds.expandedKeys, optKey];
                    this.setState(prev => ({ selects: [...prev.selects] }));
                  }}
                  onTreeExpand={exKeys => {
                    ds.expandedKeys = exKeys;
                    this.setState(prev => ({ selects: [...prev.selects] }));
                  }}
                  treeExpandedKeys={ds.searching ? undefined : ds.expandedKeys}
                />
                {this.showLegendCheckbox &&
                  <Tooltip title="Show legend below graph on dashboard">
                    <Checkbox checked={!!ds.legend} disabled={AbstractDashboardSelect.isDisabled(ds)}
                      onChange={e => this.onLegendChange(ds, e.target.checked)}>Legend</Checkbox>
                  </Tooltip>}
                {this.renderRemoveButton(ds, 'Remove from this dashboard', AbstractDashboardSelect.isDisabled(ds))}
              </div>)}
            </Space>,
          },
          ...(this.grafanaEnabled ? [{
            key: PanelID.GRAFANA,
            label: `Export ${this.itemLabel} to Grafana`,
            children: <>
              <Space orientation="vertical" className="xm-abstract-dashboard-select-modal__panel-content" onClick={e => {
                const el = e.target as HTMLElement;
                if (el.closest('.ant-tree-select,.ant-select-tree,.ant-dropdown-menu')) return;
                this.setState(prev => ({ grafanaSelects: AbstractDashboardSelect.closed(prev.grafanaSelects) }));
              }}>
                {this.state.grafanaSelects.map(ds => <div key={ds.selectId} className="xm-abstract-dashboard-select-modal__row">
                  <TreeSelect
                    className="xm-abstract-dashboard-select-modal__tree-select"
                    value={ds.value}
                    status={ds.failed ? 'error' : undefined}
                    disabled={readonly || this.isGrafana(ds)}
                    classNames={{ popup: { root: "xm-dashboard-select" } }}
                    virtual={false}
                    placeholder="Select dashboard"
                    onChange={(value: string) => this.onChange(value, ds, this.state.grafanaOptions)}
                    showSearch={{ treeNodeFilterProp: 'titleText' }}
                    treeData={this.state.grafanaOptions}
                    open={this.state.grafanaSelects.find(c => c.selectId === ds.selectId)?.open}
                    onClick={ev => {
                      const el = ev.target as Element;
                      if (ds.open && el.tagName === 'INPUT') return;
                      if (el.closest('.ant-tree-select')) this.setOpen(!ds.open, ds);
                    }}
                    treeDefaultExpandedKeys={[AbstractDashboardSelect.GRAFANA_PREFIX + PanelID.GRAFANA]}
                  />
                  {this.renderRemoveButton(ds, 'Remove from this location', readonly)}
                </div>)}
              </Space>
              <div className="xm-abstract-dashboard-select-modal__grafana-links">
                <a href="https://xormon.com/grafana.php" target="_blank">XorMon to Grafana documentation</a>
                {this.state.grafanaTrial && <a href="https://xormon.com/trial-form.php" target="_blank" className="xm-abstract-dashboard-select-modal__grafana-trial-link">Ask for XorMon REST API license</a>}
              </div>
            </>,
          }] : []),
        ]} />
      </div>
    </XmModal>;
  }
}
